<template>
  <div>
    <div>
      <CustomLoader v-if="isLoading" :width="70"></CustomLoader>
    </div>
    
    <b-card class="mainCardBG">
      <b-card-text>
        <!-- Header Navigation -->
        <b-navbar
          class="formCard"
          style="border-radius: 5px !important; padding-left: 0px"
          toggleable="lg"
          type="dark"
          variant="primary"
        >
          <b-navbar-nav>
            <b-button @click="goBack" size="sm" variant="light">
              <md-icon icon="gear-fill" aria-hidden="true">skip_previous</md-icon>Back
            </b-button>
          </b-navbar-nav>
          <b-navbar-brand center style="margin-left: 0.5rem !important;">
            <md-icon icon="gear-fill" aria-hidden="true">person</md-icon>Driver Management System
          </b-navbar-brand>
          <b-navbar-nav class="ml-auto">
            <b-button @click="openAddDriverModal" size="sm" variant="outline-light">
              <md-icon icon="gear-fill" aria-hidden="true">add</md-icon>Add Driver
            </b-button>
          </b-navbar-nav>
        </b-navbar>

        <!-- Statistics Cards -->
        <b-card class="formCard mt-3">
          <b-card-text>
            <div class="row">
              <div class="col-lg-3 col-md-6 mb-3">
                <div class="stat-card bg-primary text-white">
                  <div class="stat-icon">
                    <md-icon icon="gear-fill" aria-hidden="true">people</md-icon>
                  </div>
                  <div class="stat-content">
                    <h3>{{ driverStats.total }}</h3>
                    <p>Total Drivers</p>
                  </div>
                </div>
              </div>
              <div class="col-lg-3 col-md-6 mb-3">
                <div class="stat-card bg-success text-white">
                  <div class="stat-icon">
                    <md-icon icon="gear-fill" aria-hidden="true">check_circle</md-icon>
                  </div>
                  <div class="stat-content">
                    <h3>{{ driverStats.active }}</h3>
                    <p>Active Drivers</p>
                  </div>
                </div>
              </div>
              <div class="col-lg-3 col-md-6 mb-3">
                <div class="stat-card bg-warning text-dark">
                  <div class="stat-icon">
                    <md-icon icon="gear-fill" aria-hidden="true">schedule</md-icon>
                  </div>
                  <div class="stat-content">
                    <h3>{{ driverStats.pending }}</h3>
                    <p>Pending Verification</p>
                  </div>
                </div>
              </div>
              <div class="col-lg-3 col-md-6 mb-3">
                <div class="stat-card bg-info text-white">
                  <div class="stat-icon">
                    <md-icon icon="gear-fill" aria-hidden="true">credit_card</md-icon>
                  </div>
                  <div class="stat-content">
                    <h3>{{ driverStats.licensed }}</h3>
                    <p>Licensed Drivers</p>
                  </div>
                </div>
              </div>
            </div>
          </b-card-text>
        </b-card>

        <!-- Search and Filter Section -->
        <b-card class="formCard mt-3">
          <b-card-text>
            <div class="row">
              <div class="col-md-8">
                <div class="search-container">
                  <md-icon icon="gear-fill" aria-hidden="true" class="search-icon">search</md-icon>
                  <b-form-input 
                    v-model="searchQuery" 
                    placeholder="Search drivers by name, license, or contact..."
                    class="search-input"
                  ></b-form-input>
                </div>
              </div>
              <div class="col-md-4">
                <div class="filter-container">
                  <b-form-select 
                    v-model="statusFilter" 
                    :options="statusOptions"
                    class="me-2"
                  ></b-form-select>
                  <b-form-select 
                    v-model="typeFilter" 
                    :options="typeOptions"
                  ></b-form-select>
                </div>
              </div>
            </div>
          </b-card-text>
        </b-card>

        <!-- Drivers Table -->
        <b-card class="formCard mt-3">
          <b-card-text>
            <div class="table-responsive">
              <table class="table table-hover">
                <thead class="table-dark">
                  <tr>
                    <th>Driver Info</th>
                    <th>Contact Details</th>
                    <th>License Information</th>
                    <th>Employment Details</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="driver in filteredDrivers" :key="driver.driverId" class="driver-row">
                    <td>
                      <div class="driver-info">
                        <div class="driver-avatar">
                          <img :src="driver.photo || '/assets/default-avatar.png'" alt="Driver Photo">
                        </div>
                        <div class="driver-details">
                          <h6 class="driver-name">{{ driver.driverName }}</h6>
                          <p class="driver-id">ID: {{ driver.driverId }}</p>
                          <b-badge 
                            :variant="driver.driverType === '1' ? 'success' : 'warning'"
                            class="driver-type"
                          >
                            {{ driver.driverType === '1' ? 'Permanent' : 'Temporary' }}
                          </b-badge>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div class="contact-info">
                        <p><md-icon icon="gear-fill" aria-hidden="true" class="text-primary me-2">phone</md-icon>{{ driver.driverContact }}</p>
                        <p><md-icon icon="gear-fill" aria-hidden="true" class="text-success me-2">location_on</md-icon>{{ driver.driverLocation || 'N/A' }}</p>
                        <p><md-icon icon="gear-fill" aria-hidden="true" class="text-info me-2">email</md-icon>{{ driver.email || 'N/A' }}</p>
                      </div>
                    </td>
                    <td>
                      <div class="license-info">
                        <p><strong>License:</strong> {{ driver.license_no || 'N/A' }}</p>
                        <p><strong>Valid Until:</strong> {{ formatDate(driver.licenseValidUntil) }}</p>
                        <p><strong>Class:</strong> {{ driver.licenseClass || 'N/A' }}</p>
                      </div>
                    </td>
                    <td>
                      <div class="employment-info">
                        <p><strong>Vehicle:</strong> {{ driver.vehicleNumber || 'N/A' }}</p>
                        <p><strong>Joining Date:</strong> {{ formatDate(driver.driverDateOfJoining) }}</p>
                        <p><strong>Experience:</strong> {{ driver.driverWorkingYear || 'N/A' }} years</p>
                      </div>
                    </td>
                    <td>
                      <b-badge 
                        :variant="getStatusVariant(driver.isDriverActive)"
                        class="status-badge"
                      >
                        {{ getStatusText(driver.isDriverActive) }}
                      </b-badge>
                    </td>
                    <td>
                      <div class="action-buttons">
                        <b-button size="sm" variant="outline-primary" @click="viewDriver(driver)" class="me-1">
                          <md-icon icon="gear-fill" aria-hidden="true">visibility</md-icon>
                        </b-button>
                        <b-button size="sm" variant="outline-warning" @click="editDriver(driver)" class="me-1">
                          <md-icon icon="gear-fill" aria-hidden="true">edit</md-icon>
                        </b-button>
                        <b-button size="sm" variant="outline-info" @click="viewLicense(driver)" class="me-1">
                          <md-icon icon="gear-fill" aria-hidden="true">credit_card</md-icon>
                        </b-button>
                        <b-button size="sm" variant="outline-danger" @click="deleteDriver(driver)">
                          <md-icon icon="gear-fill" aria-hidden="true">delete</md-icon>
                        </b-button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-card-text>
        </b-card>
      </b-card-text>
    </b-card>
  </div>
</template>

<script>
import CustomLoader from "../../common/customLoader.vue";

export default {
  name: 'DriverManagement',
  components: {
    CustomLoader
  },
  data() {
    return {
      isLoading: false,
      searchQuery: '',
      statusFilter: '',
      typeFilter: '',
      statusOptions: [
        { value: '', text: 'All Status' },
        { value: 'active', text: 'Active' },
        { value: 'inactive', text: 'Inactive' },
        { value: 'pending', text: 'Pending' }
      ],
      typeOptions: [
        { value: '', text: 'All Types' },
        { value: 'permanent', text: 'Permanent' },
        { value: 'temporary', text: 'Temporary' }
      ],
      // Static data based on the database schema
      drivers: [
        {
          driverId: 1,
          driver_fname: 'Rajesh',
          driver_mname: 'Kumar',
          driver_lname: 'Sharma',
          license_no: 'DL0120140149646',
          org_license_no: 'DL0120140149646',
          driverType: '1',
          vehicleTruckId: 101,
          driverName: 'Rajesh Kumar Sharma',
          driverContact: '9876543210',
          driverWorkingYear: '5',
          driverEsic: 'ESI123456789',
          accountNo: 'ACC001',
          driverLocation: 'Mumbai',
          driverECode: 'EMP001',
          driverVehicleNumber: 'MH01AB1234',
          vehicleNumber: 'MH01AB1234',
          driverDateOfJoining: '2019-03-15',
          driverDateOfLeaving: null,
          driverDateOfBirth: '1985-06-20',
          driverAadharNumber: '1234-5678-9012',
          driverUANNumber: 'UAN123456789',
          isDriverActive: 1,
          licenseValidUntil: '2025-12-31',
          licenseClass: 'LMV-NT',
          email: 'rajesh.sharma@example.com',
          photo: '/assets/driver1.jpg'
        },
        {
          driverId: 2,
          driver_fname: 'Amit',
          driver_mname: '',
          driver_lname: 'Patel',
          license_no: 'DL0220150159647',
          org_license_no: 'DL0220150159647',
          driverType: '1',
          vehicleTruckId: 102,
          driverName: 'Amit Patel',
          driverContact: '9876543211',
          driverWorkingYear: '3',
          driverEsic: 'ESI123456790',
          accountNo: 'ACC002',
          driverLocation: 'Delhi',
          driverECode: 'EMP002',
          driverVehicleNumber: 'DL01CD5678',
          vehicleNumber: 'DL01CD5678',
          driverDateOfJoining: '2020-08-10',
          driverDateOfLeaving: null,
          driverDateOfBirth: '1988-12-15',
          driverAadharNumber: '2345-6789-0123',
          driverUANNumber: 'UAN123456790',
          isDriverActive: 1,
          licenseValidUntil: '2026-06-30',
          licenseClass: 'HMV',
          email: 'amit.patel@example.com',
          photo: '/assets/driver2.jpg'
        },
        {
          driverId: 3,
          driver_fname: 'Suresh',
          driver_mname: 'Kumar',
          driver_lname: 'Verma',
          license_no: 'DL0320160169648',
          org_license_no: 'DL0320160169648',
          driverType: '2',
          vehicleTruckId: 103,
          driverName: 'Suresh Kumar Verma',
          driverContact: '9876543212',
          driverWorkingYear: '2',
          driverEsic: 'ESI123456791',
          accountNo: 'ACC003',
          driverLocation: 'Bangalore',
          driverECode: 'EMP003',
          driverVehicleNumber: 'KA01EF9012',
          vehicleNumber: 'KA01EF9012',
          driverDateOfJoining: '2021-01-20',
          driverDateOfLeaving: null,
          driverDateOfBirth: '1990-03-25',
          driverAadharNumber: '3456-7890-1234',
          driverUANNumber: 'UAN123456791',
          isDriverActive: 0,
          licenseValidUntil: '2024-09-15',
          licenseClass: 'LMV-NT',
          email: 'suresh.verma@example.com',
          photo: '/assets/driver3.jpg'
        },
        {
          driverId: 4,
          driver_fname: 'Priya',
          driver_mname: '',
          driver_lname: 'Singh',
          license_no: 'DL0420170179649',
          org_license_no: 'DL0420170179649',
          driverType: '2',
          vehicleTruckId: 104,
          driverName: 'Priya Singh',
          driverContact: '9876543213',
          driverWorkingYear: '1',
          driverEsic: 'ESI123456792',
          accountNo: 'ACC004',
          driverLocation: 'Chennai',
          driverECode: 'EMP004',
          driverVehicleNumber: 'TN01GH3456',
          vehicleNumber: 'TN01GH3456',
          driverDateOfJoining: '2022-06-15',
          driverDateOfLeaving: null,
          driverDateOfBirth: '1992-08-10',
          driverAadharNumber: '4567-8901-2345',
          driverUANNumber: 'UAN123456792',
          isDriverActive: 1,
          licenseValidUntil: '2027-03-20',
          licenseClass: 'LMV-NT',
          email: 'priya.singh@example.com',
          photo: '/assets/driver4.jpg'
        },
        {
          driverId: 5,
          driver_fname: 'Rahul',
          driver_mname: 'Kumar',
          driver_lname: 'Gupta',
          license_no: 'DL0520180189650',
          org_license_no: 'DL0520180189650',
          driverType: '1',
          vehicleTruckId: 105,
          driverName: 'Rahul Kumar Gupta',
          driverContact: '9876543214',
          driverWorkingYear: '4',
          driverEsic: 'ESI123456793',
          accountNo: 'ACC005',
          driverLocation: 'Hyderabad',
          driverECode: 'EMP005',
          driverVehicleNumber: 'TS01IJ7890',
          vehicleNumber: 'TS01IJ7890',
          driverDateOfJoining: '2019-11-05',
          driverDateOfLeaving: null,
          driverDateOfBirth: '1987-04-18',
          driverAadharNumber: '5678-9012-3456',
          driverUANNumber: 'UAN123456793',
          isDriverActive: 1,
          licenseValidUntil: '2026-12-31',
          licenseClass: 'HMV',
          email: 'rahul.gupta@example.com',
          photo: '/assets/driver5.jpg'
        }
      ]
    }
  },
  computed: {
    filteredDrivers() {
      let filtered = this.drivers;

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(driver => 
          driver.driverName.toLowerCase().includes(query) ||
          driver.license_no.toLowerCase().includes(query) ||
          driver.driverContact.includes(query) ||
          driver.driverLocation.toLowerCase().includes(query)
        );
      }

      // Status filter
      if (this.statusFilter) {
        if (this.statusFilter === 'active') {
          filtered = filtered.filter(driver => driver.isDriverActive === 1);
        } else if (this.statusFilter === 'inactive') {
          filtered = filtered.filter(driver => driver.isDriverActive === 0);
        }
      }

      // Type filter
      if (this.typeFilter) {
        if (this.typeFilter === 'permanent') {
          filtered = filtered.filter(driver => driver.driverType === '1');
        } else if (this.typeFilter === 'temporary') {
          filtered = filtered.filter(driver => driver.driverType === '2');
        }
      }

      return filtered;
    },
    driverStats() {
      const total = this.drivers.length;
      const active = this.drivers.filter(d => d.isDriverActive === 1).length;
      const pending = this.drivers.filter(d => !d.license_no).length;
      const licensed = this.drivers.filter(d => d.license_no).length;

      return { total, active, pending, licensed };
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-IN');
    },
    getStatusVariant(status) {
      return status === 1 ? 'success' : 'danger';
    },
    getStatusText(status) {
      return status === 1 ? 'Active' : 'Inactive';
    },
    goBack() {
      this.$router.go(-1);
    },
    openAddDriverModal() {
      console.log('Opening add driver modal');
    },
    editDriver(driver) {
      console.log('Editing driver:', driver);
    },
    viewDriver(driver) {
      console.log('Viewing driver:', driver);
      // Navigate to driver detail view
      this.$router.push(`/vehicle/driverProfile?id=${driver.driverId}`);
    },
    viewLicense(driver) {
      console.log('Viewing license for:', driver);
    },
    deleteDriver(driver) {
      this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete ${driver.driverName}?`,
        {
          title: 'Confirm Deletion',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'YES',
          cancelTitle: 'NO',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      ).then(value => {
        if (value) {
          const index = this.drivers.findIndex(d => d.driverId === driver.driverId);
          if (index > -1) {
            this.drivers.splice(index, 1);
            this.$bvToast.toast('Driver deleted successfully', {
              title: 'Success',
              variant: 'success',
              solid: true
            });
          }
        }
      });
    }
  }
}
</script>

<style scoped>
.stat-card {
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 20px;
}

.stat-content h3 {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
}

.stat-content p {
  margin: 0;
  font-weight: 500;
  opacity: 0.9;
}

.search-container {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  z-index: 10;
}

.search-input {
  padding-left: 45px;
  border-radius: 25px;
  border: 2px solid #e9ecef;
  transition: border-color 0.3s ease;
}

.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.filter-container {
  display: flex;
  gap: 10px;
}

.filter-container .form-select {
  border-radius: 25px;
  border: 2px solid #e9ecef;
  transition: border-color 0.3s ease;
}

.filter-container .form-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.custom-table {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.custom-table .table {
  margin-bottom: 0;
}

.driver-info {
  display: flex;
  align-items: center;
}

.driver-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 15px;
  border: 3px solid #007bff;
}

.driver-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.driver-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
}

.driver-id {
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 5px;
}

.driver-type {
  font-size: 0.8rem;
}

.contact-info p,
.license-info p,
.employment-info p {
  margin-bottom: 5px;
  color: #2c3e50;
  font-size: 0.9rem;
}

.contact-info i,
.license-info i,
.employment-info i {
  width: 16px;
}

.status-badge {
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.action-buttons {
  display: flex;
  gap: 5px;
}

.action-buttons .btn {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.action-buttons .btn:hover {
  transform: scale(1.1);
}

/* Responsive Design */
@media (max-width: 768px) {
  .stat-card {
    padding: 15px;
  }
  
  .stat-content h3 {
    font-size: 1.5rem;
  }
  
  .filter-container {
    flex-direction: column;
  }
  
  .driver-info {
    flex-direction: column;
    text-align: center;
  }
  
  .driver-avatar {
    margin-right: 0;
    margin-bottom: 10px;
  }
  
  .action-buttons {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .custom-table {
    font-size: 0.9rem;
  }
}

@media (max-width: 576px) {
  .stat-card {
    padding: 10px;
  }
  
  .stat-icon {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
  
  .stat-content h3 {
    font-size: 1.2rem;
  }
  
  .action-buttons .btn {
    width: 30px;
    height: 30px;
  }
}
</style> 


// WEBPACK FOOTER //
// DriverManagement.vue?46596637